<template>
  <v-container fluid>
    <v-layout column>
      <v-layout row align-center>
        <!-- row of filters and button -->
        <v-layout column>
          <v-layout row justify-space-between>
            <v-flex class="mr-2">
              <template>
                <v-flex>
                  <v-menu
                    ref="startDateMenu"
                    v-model="startDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <template slot="activator" slot-scope="{ on }">
                      <v-text-field
                        label="Start Date"
                        format="dddd, MM DD YYYY"
                        @change="value => (startDate = value)"
                        :value="dateFormatted(startDate)"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      :show-current="endDate"
                      v-model="startDate"
                      @input="startDateMenu = false"
                      no-title
                      scrollable
                    >
                    </v-date-picker>
                  </v-menu>
                </v-flex>
              </template>
            </v-flex>
            <v-flex class="mr-2">
              <template>
                <v-flex>
                  <v-menu
                    ref="endDateMenu"
                    v-model="endDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <template slot="activator" slot-scope="{ on }">
                      <v-text-field
                        label="End Date"
                        format="dddd, MM DD YYYY"
                        @change="value => (endDate = value)"
                        :value="dateFormatted(endDate)"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      :show-current="startDate"
                      v-model="endDate"
                      @input="endDateMenu = false"
                      no-title
                      scrollable
                    >
                    </v-date-picker>
                  </v-menu>
                </v-flex>
              </template>
            </v-flex>
            <v-flex shrink class="mr-2">
              <v-select
                label="Shift Status"
                :items="statusFilters"
                v-model="selectedStatusFilter"
                clearable
              ></v-select>
            </v-flex>
            <v-flex shrink class="mr-2">
              <v-select
                label="Position"
                multiple
                chips
                :items="positionFilters"
                item-text="name"
                item-value="id"
                v-model="selectedPositionFilter"
                :loading="positionsLoading"
                :disabled="positionsLoading"
                clearable
              ></v-select>
            </v-flex>
            <v-flex>
              <v-select
                label="Per Page"
                :items="limitOptions"
                v-model="limit"
              ></v-select>
            </v-flex>
          </v-layout>

          <v-layout
            row
            justify-end
            v-if="positionFilterIncludesCVT || positionFilterIncludesAssistant"
          >
            <v-flex shrink class="mr-2" v-if="positionFilterIncludesCVT">
              <v-switch label="Only NG" v-model="onlyNG" />
            </v-flex>
            <v-flex shrink class="mr-2" v-if="positionFilterIncludesCVT">
              <v-switch label="Exclude NG" v-model="excludeNG" />
            </v-flex>
            <v-flex shrink class="mr-2" v-if="positionFilterIncludesAssistant">
              <v-switch label="Only Advanced Assistant" v-model="onlyAA" />
            </v-flex>
          </v-layout>

          <v-layout row>
            <v-flex shrink class="mr-2">
              <v-autocomplete
                label="Location"
                multiple
                chips
                :items="locationFilters"
                item-text="name"
                item-value="id"
                v-model="selectedLocationFilter"
                :loading="locationsLoading"
                :disabled="locationsLoading"
                clearable
              ></v-autocomplete>
              <v-switch label="My Locations" v-model="managedLocations">
              </v-switch>
            </v-flex>
            <v-flex shrink class="mr-2">
              <v-select
                label="State"
                :items="stateFilterOptions"
                v-model="selectedStateFilter"
                clearable
              ></v-select>
            </v-flex>
            <v-flex shrink class="mr-2">
              <v-autocomplete
                label="Employee"
                :items="employeeFilters"
                item-text="name"
                item-value="id"
                v-model="selectedEmployeeFilter"
                :loading="employeesLoading"
                :disabled="employeesLoading"
                clearable
              ></v-autocomplete>
              <v-switch label="My Employees" v-model="managedEmployees">
              </v-switch>
            </v-flex>
            <v-flex shrink class="mr-2">
              <v-select
                label="Bonus"
                :items="bonusFilterOptions"
                v-model="selectedbonusFilter"
                clearable
              ></v-select>
            </v-flex>
            <v-flex shrink class="mr-2">
              <v-switch
                v-model="selectedTradeFilter"
                label="View Shift Trades"
              ></v-switch>
            </v-flex>
          </v-layout>
        </v-layout>
        <v-layout row>
          <v-btn @click="reGetRecords" color="primary">
            Update
          </v-btn>
        </v-layout>
      </v-layout>

      <v-divider />

      <FilterPresets
        :currentQuery="formattedQuery"
        @updateFilters="updateFilters"
      />

      <v-layout align-center justify-space-between class="my-2">
        <v-flex shrink>
          <a href="#" @click.prevent="goToMonth">Switch To Month View</a>
          <SetDefaultViewButton view="list" class="ml-3" />
        </v-flex>
        <v-flex shrink>
          <v-btn :to="`/events`" color="info">
            View All Events
          </v-btn>
          <v-btn :to="`/event/new`" color="success">
            <v-icon left>add</v-icon>
            Create Event
          </v-btn>
          <v-btn :to="`/service/new`" color="success">
            <v-icon left>add</v-icon>
            Create Service Request
          </v-btn>
        </v-flex>
      </v-layout>

      <v-flex v-if="shiftQueryPending">
        <v-progress-linear indeterminate> </v-progress-linear>
      </v-flex>

      <v-flex v-else>
        <v-layout column justify-center class="text-xs-center">
          <v-flex class="mb-2">
            <span class="subtle body"
              >{{ `${queryTotal} record${queryTotal == 1 ? "" : "s"}` }} in this
              search</span
            >
          </v-flex>
          <v-layout row>
            <v-flex>
              <v-pagination v-model="page" :length="pageLength"> </v-pagination>
            </v-flex>
          </v-layout>
        </v-layout>

        <v-layout column>
          <v-layout row>
            <v-flex shrink>
              <div>
                <a href="#" @click.prevent="selectAll">
                  Select all
                </a>
              </div>
              <div></div>
              <a href="#" @click.prevent="selectNone">
                Select none
              </a>
            </v-flex>
            <v-spacer />
          </v-layout>

          <v-flex v-for="(shift, i) in shifts" :key="shift.id">
            <v-layout row align-center justify-space-between>
              <v-flex shrink class="text-xs-center">
                <v-checkbox
                  :value="bulkSelectedIDs.includes(shift.id)"
                  @change="addOrRemoveBulkSelection(shift.id, ...arguments)"
                />
              </v-flex>
              <v-flex grow>
                <ShiftInList :index="i" :shift="shift" />
              </v-flex>
            </v-layout>
          </v-flex>

          <ShiftBulkEdit
            v-show="bulkSelectedShifts.length > 0"
            :shifts="bulkSelectedShifts"
          />
        </v-layout>

        <v-layout column>
          <v-flex v-for="(shift, i) in sortedShifts" :key="shift.id">
            <ShiftInList :index="i" :shift="shift" />
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import WeekView from "@/components/calendar/weekView.vue";
import MonthView from "@/components/calendar/monthView.vue";
import shiftOnMonth from "@/components/calendar/shiftOnMonth.vue";
import shiftOnWeek from "@/components/calendar/shiftOnWeek.vue";
import ShiftInList from "@/components/calendar/shift-in-list.vue";
import collectionApi from "@/api/collections";
import FilterPresets from "@/components/calendar/filter-presets.vue";
import ShiftBulkEdit from "@/components/shifts/shift-bulk-edit.vue";
import SetDefaultViewButton from "@/components/calendar/set-default-view-button.vue";

import { mapState, mapGetters } from "vuex";

export default {
  components: {
    WeekView,
    MonthView,
    shiftOnMonth,
    shiftOnWeek,
    ShiftInList,
    FilterPresets,
    ShiftBulkEdit,
    SetDefaultViewButton,
  },
  created() {
    this.setUp();
  },
  watch: {
    page: {
      handler(value) {
        if (value != this.$route.query.page) {
          this.reGetRecords();
        } else {
        }
      }
    },
    selectedEmployeeFilter: {
      handler(value) {
        if (value) {
          this.managedEmployees = false;
        }
      }
    },
    managedEmployees: {
      handler(value) {
        if (value) {
          this.selectedEmployeeFilter = null;
        }
      }
    },
    selectedLocationFilter: {
      handler(value) {
        if (value) {
          this.managedLocations = false;
        }
      }
    },
    managedLocations: {
      handler(value) {
        if (value) {
          this.selectedLocationFilter = null;
        }
      }
    },
    onlyAA(on) {
      this.onlyAA = on;
    },
    onlyNG(on) {
      if (on) {
        this.excludeNG = false;
      }
    },
    excludeNG(on) {
      if (on) {
        this.onlyNG = false;
      }
    }
  },
  computed: {
    ...mapState("userAuth", ["user"]),
    haveShifts: function() {
      return this.shifts.length > 0;
    },
    positionFilterIncludesCVT() {
      const cvtID = this.positionFilters.find(p => p.name === "CVT")?.id;
      if (cvtID && this.selectedPositionFilter.includes(cvtID)) {
        return true;
      }
      return false;
    },
    positionFilterIncludesAssistant() {
      const assistantID = this.positionFilters.find(p => p.name === "Assistant")
        ?.id;
      if (assistantID && this.selectedPositionFilter.includes(assistantID)) {
        return true;
      }
      return false;
    },
    availableManagers: function() {
      return [];
    },
    computedOffset: function() {
      return this.limit * (this.page - 1);
    },
    pageLength: function() {
      return Math.ceil(this.queryTotal / this.limit);
    },
    formattedQuery: function() {
      return {
        startDate: this.startDate,
        endDate: this.endDate,
        limit: this.limit,
        offset: this.computedOffset,
        page: this.page,
        managedLocations: this.managedLocations,
        managedEmployees: this.managedEmployees,
        statusFilter: this.selectedStatusFilter,
        state: this.selectedStateFilter,
        positionFilter: this.selectedPositionFilter,
        locationFilter: this.selectedLocationFilter,
        employeeFilter: this.selectedEmployeeFilter,
        bonusFilter: this.selectedbonusFilter,
        tradeFilter: this.selectedTradeFilter,
        onlyAA: this.onlyAA,
        onlyNG: this.onlyNG,
        excludeNG: this.excludeNG
      };
    },
    sortedShifts: function() {
      var dateGrouped = _.groupBy(this.shifts, shift => {
        return this.$moment(shift.startDatetime).format("YYYY-MM-DD");
      }); // groups all the same-date shifts together like:
      /*
      {
        2020-04-19: [
          shift1,
          shift2,
        ],
        2020-04-20: [
          shift1,
          shift2,
          shift3,
        ]
      }
      */

      var tradeSorted = _.each(dateGrouped, (shiftGroup, key) => {
        // for each group of shifts...
        dateGrouped[key] = _.sortBy(shiftGroup, shift => {
          // sort this subset of shifts and assign them back tion the date-grouped object
          // if (shift.userId === this.user.id) {
          //   return -3
          // }
          if (shift.tradePending) {
            return -2;
          }
          return 0;
        });
      });
      var sortedShifts = [];
      // go through the date-grouped object and pull out all the shfits in order,
      // pushing them into the empty array above
      _.each(tradeSorted, shiftGroup => {
        _.each(shiftGroup, shift => {
          sortedShifts.push(shift);
        });
      });
      return sortedShifts;
    },

    bulkSelectedShifts() {
      return this.shifts.filter(s => this.bulkSelectedIDs.includes(s.id));
    }
  },
  methods: {
    updateFilters(e) {
      this.selectedStatusFilter = e["statusFilter"];
      this.selectedPositionFilter = e["positionFilter"];
      this.selectedLocationFilter = e["locationFilter"];
      this.selectedStateFilter = e["state"];
      this.selectedEmployeeFilter = e["employeeFilter"];
      this.selectedbonusFilter = e["bonusFilter"];
      this.selectedTradeFilter = e["tradeFilter"];
      this.onlyAA = e["onlyAA"];
      this.onlyNG = e["onlyNG"];
      this.excludeNG = e["excludeNG"];
      this.selectedTradeFilter = e["tradeFilter"];

      this.managedEmployees = e["managedEmployees"];
      this.managedLocations = e["managedLocations"];

      this.reGetRecords();
    },

    selectAll() {
      this.bulkSelectedIDs = this.shifts.map(s => s.id);
    },

    selectNone() {
      this.bulkSelectedIDs = [];
    },

    addOrRemoveBulkSelection(shiftID, adding) {
      if (adding) {
        this.bulkSelectedIDs.push(shiftID);
      } else {
        this.bulkSelectedIDs = this.bulkSelectedIDs.filter(
          id => id !== shiftID
        );
      }
    },

    goToMonth: function() {
      // don't pass these dates into the calendar, but keep the other filters
      let query = {
        ...this.formattedQuery,
        startDate: undefined,
        endDate: undefined
      };
      this.$router.push({
        name: "month",
        query: query
      });
    },

    reGetRecords: function() {
      this.$router.push({
        name: "calendar",
        query: this.formattedQuery
      });
    },

    getShifts: function() {
      const params = this.formattedQuery;

      this.shiftQueryPending = true;
      this.$store
        .dispatch("shifts/getList", params)
        .then(res => {
          this.queryTotal = res.total;
          this.shifts = res.shifts;
        })
        .finally(res => {
          this.shiftQueryPending = false;
        });
    },

    dateFormatted: function(date) {
      return this.$moment(date).format("YYYY-MM-DD");
    },

    setUp: function() {
      // use all dates and params in the url if any are present
      // otherwise, defaults are used from the component's data
      this.startDate = this.$route.query.startDate || this.startDate;
      this.endDate = this.$route.query.endDate || this.endDate;
      this.limit = parseInt(this.$route.query.limit) || this.limit;
      this.page = parseInt(this.$route.query.page) || this.page;
      this.selectedStatusFilter =
        this.$route.query.statusFilter || this.selectedStatusFilter;

      const queryPosition = this.$route.query.positionFilter;
      if (queryPosition && Array.isArray(queryPosition)) {
        this.selectedPositionFilter = queryPosition.map(id => parseInt(id));
      }

      this.selectedLocationFilter =
        (this.$route.query.locationFilter
          ? this.$route.query.locationFilter.map(id => parseInt(id))
          : null) || this.selectedLocationFilter;
      this.selectedStateFilter =
        this.$route.query.state || this.selectedStateFilter;
      this.selectedEmployeeFilter =
        parseInt(this.$route.query.employeeFilter) ||
        this.selectedEmployeeFilter;
      this.selectedbonusFilter =
        this.$route.query.bonusFilter || this.selectedbonusFilter;
      this.selectedTradeFilter =
        this.$route.query.tradeFilter || this.selectedTradeFilter;

      this.onlyAA = this.$route.query.onlyAA || this.onlyAA;
      this.onlyNG = this.$route.query.onlyNG || this.onlyNG;
      this.excludeNG = this.$route.query.excludeNG || this.excludeNG;

      // this is bonkers:
      if (typeof this.$route.query.managedLocations == "undefined") {
        this.managedLocations = true;
      } else {
        this.managedLocations =
          this.$route.query.managedLocations === "true" ||
          (this.$route.query.managedLocations &&
            this.$route.query.managedLocations !== "false")
            ? true
            : false;
      }

      this.managedEmployees =
        this.$route.query.managedEmployees || this.managedEmployees;

      this.$router.push({
        name: "calendar",
        query: this.formattedQuery
      });

      this.getShifts();

      this.positionsLoading = true;
      collectionApi.getPositions(
        positions => {
          this.positionFilters = positions;
          this.positionsLoading = false;
        },
        error => {
          console.log(error);
          this.positionsLoading = false;
        }
      );

      this.locationsLoading = true;
      collectionApi.getLocations(
        locations => {
          this.locationFilters = locations;
          this.locationsLoading = false;
        },
        error => {
          console.log(error);
          this.locationsLoading = false;
        }
      );

      this.employeesLoading = true;
      collectionApi.getEmployees(
        employees => {
          this.employeeFilters = employees;
          this.employeesLoading = false;
        },
        error => {
          console.log(error);
          this.employeesLoading = false;
        }
      );

      // TODO: get all filter content
      // TODO: get all managers
      // this.$store.dispatch('serviceRequest/getOptions', this.user.id)

      // this.setNewDates(start, end)
      // this.setNewMonth(currentMonthName)
    },
    shiftStartTimeString: function(shift) {
      return this.$moment(shift.startDatetime).format("HH:MM");
    },

    shiftDuration: function(shift) {
      var start = this.$moment(shift.startDatetime);
      var end = this.$moment(shift.endDatetime);
      var duration = this.$moment.duration(end.diff(start));
      var minutes = duration.asMinutes();

      return minutes;
    }
  },

  data: function() {
    return {
      queryTotal: null,
      limitOptions: [10, 25, 50, 100],
      limit: 10,
      page: 1,
      bulkSelectedIDs: [],
      startDateMenu: false,
      endDateMenu: false,
      startDate: this.$moment().format("YYYY-MM-DD"),
      endDate: this.$moment()
        .add(1, "week")
        .format("YYYY-MM-DD"),
      shifts: [],
      shiftQueryPending: false,
      positionsLoading: false,
      locationsLoading: false,
      employeesLoading: false,
      statusFilters: [
        {
          text: "Open Requests",
          value: "available"
        },
        {
          text: "Assigned",
          value: "assigned"
        },
        {
          text: "Pending Approval",
          value: "pending"
        },
        {
          text: "Employee was Absent",
          value: "absent"
        }
      ],
      selectedStatusFilter: null,

      positionFilters: [],
      selectedPositionFilter: [],

      locationFilters: [],
      selectedLocationFilter: [],

      stateFilterOptions: collectionApi.getStates(),

      selectedStateFilter: null,

      employeeFilters: [],
      selectedEmployeeFilter: null,

      onlyAA: false,
      onlyNG: false,
      excludeNG: false,

      managedLocations: null,
      managedEmployees: null,

      bonusFilterOptions: [
        {
          text: "Straight Bonus",
          value: "straightBonus"
        },
        {
          text: "Call in Bonus",
          value: "callInBonus"
        },
        {
          text: "Travel Bonus",
          value: "travelBonus"
        }
      ],
      selectedbonusFilter: null,

      selectedTradeFilter: null,

      // selectedMultiFilter: null,
      // selectedMultiFilterClient: null,
      // selectedMultiFilterEmployee: null,
      multiFilterOptions: [
        {
          text: "By Client",
          value: "clients"
        },
        {
          text: "By Employee",
          value: "employees"
        }
      ]
    };
  }
};
</script>

<style scoped>
#dashboard-container {
}

.my-event {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-radius: 2px;
  border: 1px solid #1867c0;
  font-size: 12px;
  padding: 3px;
  cursor: pointer;
  margin-bottom: 1px;
  left: 4px;
  margin-right: 8px;
  position: relative;
}

.my-event.with-time {
  position: absolute;
  right: 4px;
  margin-right: 0px;
}

.pending-approval {
  opacity: 0.3;
  border: 1px dashed black !important;
  border-radius: 4px;
  padding: 4px;
}

.shift-overflow-count {
  position: absolute;
  top: 0px;
  right: 0px;
}
</style>

<style>
.vss-calendar.v-calendar.timeframe-week .v-calendar-daily__body {
  display: none !important;
}

.vss-calendar.v-calendar.timeframe-day .v-calendar-daily__body {
  display: none !important;
}

.vss-calendar .v-calendar-weekly__day {
  max-height: 140px !important;
  overflow-y: scroll !important;
  overflow-x: hidden !important;
}

.vss-calendar .v-calendar-weekly__day::-webkit-scrollbar {
  width: 0 !important;
}
.vss-calendar .v-calendar-weekly__day {
  overflow: -moz-scrollbars-none !important;
}
.vss-calendar .v-calendar-weekly__day {
  -ms-overflow-style: none !important;
}

.shift-overflow-count .v-chip__content {
  cursor: pointer !important;
}

.shift-details-icon {
  cursor: default;
}
</style>
